import React, { useMemo, useEffect, useRef, useState } from 'react';
import {
	Box,
	Grid,
	TextField,
	Button,
	MenuItem,
	Select,
	InputLabel,
	FormControl,
	FormControlLabel,
	Switch,
	Typography,
	IconButton,
	Chip,
	OutlinedInput,
	Card,
	CardContent,
	CardActions,
	Stack,
	ListItemText,
	ListItemIcon,
	Checkbox,
	alpha,
} from '@mui/material';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadIcon from '@mui/icons-material/Upload';
import RestoreIcon from '@mui/icons-material/Restore';
import { BranchOfficeType, branchOfficeTypeWithNameList, EngineEnum, INetsuiteAccount, WeekDayEnum } from '../../types';
import { ENGINE_OPTIONS, WEEK_DAY_OPTIONS } from '../../constants';
import { MapPickerModal } from '../../../common/components/MapPickerModal';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { getDefaultConfigToMap } from '../../helper';

import DirectionsBusFilledIcon from '@mui/icons-material/DirectionsBusFilled';
import DirectionsCarFilledIcon from '@mui/icons-material/DirectionsCarFilled';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import SailingIcon from '@mui/icons-material/Sailing';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import HotelIcon from '@mui/icons-material/Hotel';
import CardTravelIcon from '@mui/icons-material/CardTravel';
import HikingIcon from '@mui/icons-material/Hiking';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';

// ---------------------------------- Helpers
const accountLabel = (a: INetsuiteAccount) => `${a.id} — ${a.accountNumber} — ${a.accountName}`;
const getDistinctCurrencies = (rows: INetsuiteAccount[]) => Array.from(new Set(rows.map((r) => r.currency))).sort();
const getAccountsByCurrency = (rows: INetsuiteAccount[], currency?: string) =>
	rows.filter((r) => r.currency === currency);
const toHHmm = (t?: string) => (t ? t.slice(0, 5) : ''); // "10:00:00" -> "10:00"

// File → { rawBase64, dataUrl }
const fileToBase64Raw = (file: File): Promise<{ raw: string; dataUrl: string }> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			const dataUrl = String(reader.result || '');
			const commaIdx = dataUrl.indexOf(',');
			const raw = commaIdx >= 0 ? dataUrl.slice(commaIdx + 1) : dataUrl; // sin "data:image/...;base64,"
			resolve({ raw, dataUrl });
		};
		reader.onerror = reject;
		reader.readAsDataURL(file);
	});

const ENGINE_META: Record<EngineEnum, { label: string; color: string; Icon: React.ElementType }> = {
	[EngineEnum.BUSES]: { label: 'Buses', color: '#8E24AA', Icon: DirectionsBusFilledIcon },
	[EngineEnum.CARS]: { label: 'Autos', color: '#1565C0', Icon: DirectionsCarFilledIcon },
	[EngineEnum.CRUISES]: { label: 'Cruceros', color: '#00897B', Icon: DirectionsBoatIcon },
	[EngineEnum.FERRY]: { label: 'Ferry', color: '#00695C', Icon: SailingIcon },
	[EngineEnum.FLIGHTS]: { label: 'Vuelos', color: '#EF6C00', Icon: FlightTakeoffIcon },
	[EngineEnum.HOTELS]: { label: 'Hoteles', color: '#2E7D32', Icon: HotelIcon },
	[EngineEnum.PACKAGES]: { label: 'Paquetes', color: '#5D4037', Icon: CardTravelIcon },
	[EngineEnum.TOURS]: { label: 'Tours', color: '#3949AB', Icon: HikingIcon },
	[EngineEnum.INSURANCES]: { label: 'Seguros', color: '#C62828', Icon: HealthAndSafetyIcon },
};

// ---------------------------------- Tipos
export type PositionMapConfig = {
	latitude: number;
	longitude: number;
	countryCodeRestrictToSearch: string;
};
type PhysicalBranchForm = {
	id?: number;
	name: string;
	branchOfficeType: BranchOfficeType.PhysicalBranch;
	active: boolean;
	address: string;
	phoneNumber: string;
	lat: number | string;
	lng: number | string;
	email: string;
	image: { id?: number | string; name: string; image: string };
	schedules: { id?: number | string; weekDay: WeekDayEnum; initialTime: string; endTime: string }[];
	engines: EngineEnum[];
};

type NotPhysicalBranchForm = {
	id?: number;
	name: string;
	branchOfficeType:
		| BranchOfficeType.OfficeCashDesk
		| BranchOfficeType.BankAccount
		| BranchOfficeType.ExternalPaymentLink
		| BranchOfficeType.PhysicalPos;
	active: boolean;
	onlyAdmins: boolean;
	currencyConfigs: { currency: string; netSuiteAccountId: number | string }[];
};

export type BranchOfficeFormValues = PhysicalBranchForm | NotPhysicalBranchForm;

// ---------------------------------- Normalización defaults
function normalizeDefaults(d?: Partial<BranchOfficeFormValues>): BranchOfficeFormValues {
	if (!d) {
		return {
			name: '',
			branchOfficeType: BranchOfficeType.OfficeCashDesk,
			active: true,
			onlyAdmins: false,
			currencyConfigs: [],
		} as NotPhysicalBranchForm;
	}
	if (d.branchOfficeType === BranchOfficeType.PhysicalBranch) {
		const p = d as PhysicalBranchForm;
		return {
			id: p.id,
			name: p.name ?? '',
			branchOfficeType: BranchOfficeType.PhysicalBranch,
			active: p.active ?? true,
			address: p.address ?? '',
			phoneNumber: p.phoneNumber ?? '',
			lat: p.lat ?? '',
			lng: p.lng ?? '',
			email: p.email ?? '',
			image: p.image ?? { id: 0, name: '', image: '' }, // id por defecto 0 (creación)
			schedules: (p.schedules ?? []).map((s) => ({
				...s,
				initialTime: toHHmm(s.initialTime as any),
				endTime: toHHmm(s.endTime as any),
			})),
			engines: p.engines ?? [],
		};
	}
	const n = d as NotPhysicalBranchForm;
	return {
		id: n.id,
		name: n.name ?? '',
		branchOfficeType:
			(n.branchOfficeType as NotPhysicalBranchForm['branchOfficeType']) ?? BranchOfficeType.OfficeCashDesk,
		active: n.active ?? true,
		onlyAdmins: n.onlyAdmins ?? false,
		currencyConfigs: n.currencyConfigs ?? [],
	};
}

// ---------------------------------- Props
type BranchOfficeFormProps = {
	defaultValues?: Partial<BranchOfficeFormValues>;
	onSubmit: (values: BranchOfficeFormValues) => void;
	onCancel?: () => void;
	submitLabel?: string;
	netsuiteAccounts: INetsuiteAccount[];
};

export const BranchOfficeForm: React.FC<BranchOfficeFormProps> = ({
	defaultValues,
	onSubmit,
	onCancel,
	submitLabel = 'Guardar',
	netsuiteAccounts,
}: BranchOfficeFormProps) => {
	const initialValues = useMemo(() => normalizeDefaults(defaultValues), [defaultValues]);
	const [mapOpen, setMapOpen] = useState(false);
	const { auth } = useSelector((state: RootState) => state);
	const configToMap = getDefaultConfigToMap(auth.country);
	const {
		control,
		handleSubmit,
		watch,
		setValue,
		trigger,
		clearErrors,
		unregister,
		reset,
		formState: { errors, isSubmitting, isValid },
	} = useForm<BranchOfficeFormValues>({
		defaultValues: initialValues as any,
		mode: 'onChange',
		reValidateMode: 'onChange',
		shouldUnregister: true,
	});

	// Imagen original del backend para poder revertir
	const originalImageRef = useRef<PhysicalBranchForm['image'] | null>((initialValues as any)?.image ?? null);
	// Preview local para nuevas cargas (dataUrl completo)
	const [localPreview, setLocalPreview] = useState<string | null>(null);
	const [uploadKey, setUploadKey] = useState(0); // reset del input file

	// Rehidratar cuando llegan defaults (edición)
	useEffect(() => {
		if (!defaultValues) return;
		const normalized = normalizeDefaults(defaultValues);
		reset(normalized, { keepDirty: false, keepErrors: false });
		setLocalPreview(null); // limpiar preview local cuando rehidrato
		if (normalized.branchOfficeType === BranchOfficeType.PhysicalBranch) {
			originalImageRef.current = (normalized as PhysicalBranchForm).image ?? null;
			clearErrors(['onlyAdmins', 'currencyConfigs']);
		} else {
			originalImageRef.current = null;
			clearErrors(['address', 'phoneNumber', 'lat', 'lng', 'email', 'image', 'schedules', 'engines']);
		}
		trigger();
	}, [defaultValues, reset, clearErrors, trigger]);

	const branchOfficeType = watch('branchOfficeType');
	const isPhysicalBranch = branchOfficeType === BranchOfficeType.PhysicalBranch;

	// FieldArrays
	const {
		fields: scheduleFields,
		append: appendSchedule,
		remove: removeSchedule,
	} = useFieldArray({
		control,
		name: isPhysicalBranch ? 'schedules' : ('schedules' as any),
		keyName: 'key',
	});

	const {
		fields: currencyFields,
		append: appendCurrency,
		remove: removeCurrency,
	} = useFieldArray({ control, name: !isPhysicalBranch ? 'currencyConfigs' : ('currencyConfigs' as any) });

	// Derivados
	const distinctCurrencies = useMemo(() => getDistinctCurrencies(netsuiteAccounts), [netsuiteAccounts]);
	const currencyConfigsWatch = watch(!isPhysicalBranch ? 'currencyConfigs' : ('currencyConfigs' as any)) as
		| { currency?: string }[]
		| undefined;
	const usedCurrencies = new Set((currencyConfigsWatch ?? []).map((cc) => (cc?.currency ?? '').trim()).filter(Boolean));

	const schedulesWatch = watch(isPhysicalBranch ? 'schedules' : ('schedules' as any)) as
		| { weekDay?: WeekDayEnum }[]
		| undefined;
	const usedWeekDays = new Set((schedulesWatch ?? []).map((s) => s?.weekDay).filter(Boolean));

	// Imagen observada (para datos y nombre)
	const currentImage = (isPhysicalBranch ? watch('image') : undefined) as PhysicalBranchForm['image'] | undefined;

	// -------------------------- Submit
	const submitHandler = (raw: BranchOfficeFormValues) => {
		if (isPhysicalBranch) {
			const scheds = (raw as PhysicalBranchForm).schedules || [];
			if (scheds.length === 0) return; // mínimo 1 horario

			const imgIdRaw = (raw as PhysicalBranchForm).image?.id;
			const imageId = imgIdRaw === undefined || imgIdRaw === '' ? 0 : Number(imgIdRaw);

			const cleaned: PhysicalBranchForm = {
				...(raw as PhysicalBranchForm),
				lat: Number((raw as PhysicalBranchForm).lat),
				lng: Number((raw as PhysicalBranchForm).lng),
				image: {
					...((raw as PhysicalBranchForm).image || { id: 0, name: '', image: '' }),
					id: imageId,
				},
				// ====== AQUÍ el cambio solicitado ======
				// siempre enviar id: si existe del backend -> ese número; si es nuevo -> 0
				schedules: scheds.map((s) => ({
					id: s.id !== undefined && s.id !== '' ? Number(s.id) : 0, // <-- cambio: id 0 para nuevos
					weekDay: s.weekDay,
					initialTime: s.initialTime,
					endTime: s.endTime,
				})),
			};
			onSubmit(cleaned);
		} else {
			const notPhysical = raw as NotPhysicalBranchForm;
			if (!notPhysical.currencyConfigs || notPhysical.currencyConfigs.length === 0) return; // mínimo 1
			const cleaned: NotPhysicalBranchForm = {
				...notPhysical,
				currencyConfigs: notPhysical.currencyConfigs.map((c) => ({
					currency: c.currency,
					netSuiteAccountId: Number(c.netSuiteAccountId),
				})),
			};
			onSubmit(cleaned);
		}
	};

	// -------------------------- Cambiar tipo
	const handleTypeChange = async (value: BranchOfficeType) => {
		setValue('branchOfficeType', value as any, { shouldValidate: true });

		if (value === BranchOfficeType.PhysicalBranch) {
			setValue('onlyAdmins' as any, false);
			setValue('currencyConfigs' as any, []);
			unregister('onlyAdmins' as any);
			unregister('currencyConfigs' as any);
			clearErrors(['onlyAdmins', 'currencyConfigs']);
			setValue('engines' as any, [], { shouldValidate: true });
			// restablecer imagen original (por si venías de not-physical)
			setLocalPreview(null);
			setValue('image', originalImageRef.current ?? { id: 0, name: '', image: '' }, { shouldDirty: true });
		} else {
			setValue('onlyAdmins' as any, false, { shouldValidate: true });
			setValue('address' as any, '');
			setValue('phoneNumber' as any, '');
			setValue('lat' as any, '');
			setValue('lng' as any, '');
			setValue('email' as any, '');
			setValue('image' as any, { id: 0, name: '', image: '' });
			setValue('schedules' as any, []);
			setValue('engines' as any, []);
			unregister('address' as any);
			unregister('phoneNumber' as any);
			unregister('lat' as any);
			unregister('lng' as any);
			unregister('email' as any);
			unregister('image' as any);
			unregister('schedules' as any);
			unregister('engines' as any);
			clearErrors(['address', 'phoneNumber', 'lat', 'lng', 'email', 'image', 'schedules', 'engines']);
			setLocalPreview(null);
		}
		await trigger();
	};

	// -------------------------- Handlers imagen
	const handleUploadClick = () => {
		const input = document.getElementById('branch-image-upload') as HTMLInputElement | null;
		input?.click();
	};

	const handleFileSelected: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
		const file = e.target.files?.[0];
		if (!file) return;
		try {
			const { raw, dataUrl } = await fileToBase64Raw(file);
			const previousId =
				(originalImageRef.current?.id as number | string | undefined) ??
				(currentImage?.id as number | string | undefined);
			const nextId = previousId !== undefined && previousId !== '' ? Number(previousId) : 0;

			setValue('image', { id: nextId, name: file.name, image: raw }, { shouldDirty: true, shouldValidate: true });
			setLocalPreview(dataUrl);
			await trigger('image');
		} catch {
			// opcional: manejar error
		} finally {
			setUploadKey((k) => k + 1);
		}
	};

	const handleRevertImage = async () => {
		if (!originalImageRef.current) return;
		setValue('image', originalImageRef.current, { shouldDirty: true, shouldValidate: true });
		setLocalPreview(null);
		await trigger('image');
		setUploadKey((k) => k + 1);
	};

	const previewUrl = localPreview || currentImage?.image || '';
	const hasLocalUpload = !!localPreview;

	return (
		<Box component="form" onSubmit={handleSubmit(submitHandler)} noValidate>
			<Grid container spacing={2}>
				{/* Nombre */}
				<Grid item xs={12} md={6}>
					<Controller
						control={control}
						name="name"
						rules={{
							required: 'El nombre es requerido',
							minLength: { value: 2, message: 'Mínimo 2 caracteres' },
							maxLength: { value: 100, message: 'No puede superar 100 caracteres' },
						}}
						render={({ field }) => (
							<TextField
								{...field}
								label="Nombre"
								fullWidth
								required
								error={!!(errors as any).name}
								helperText={(errors as any).name?.message}
								inputProps={{ maxLength: 100 }}
							/>
						)}
					/>
				</Grid>

				{/* Tipo */}
				<Grid item xs={12} md={6}>
					<Controller
						control={control}
						name="branchOfficeType"
						rules={{ required: 'El tipo es requerido' }}
						render={({ field }) => (
							<FormControl fullWidth>
								<InputLabel id="branch-type-label" required>
									Tipo
								</InputLabel>
								<Select
									{...field}
									labelId="branch-type-label"
									label="Tipo"
									onChange={(e) => handleTypeChange(e.target.value as BranchOfficeType)}
								>
									{branchOfficeTypeWithNameList.map((opt) => (
										<MenuItem key={opt.id} value={opt.id}>
											{opt.name}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						)}
					/>
				</Grid>

				{/* Activo */}
				<Grid item xs={12} md={6}>
					<Controller
						control={control}
						name="active"
						render={({ field }) => (
							<FormControlLabel control={<Switch {...field} checked={!!field.value} />} label="Activo" />
						)}
					/>
				</Grid>

				{/* ========= Física ========= */}
				{isPhysicalBranch && (
					<>
						<Grid item xs={12}>
							<Typography variant="h6">Datos de sucursal física</Typography>
						</Grid>

						{/* Campos ocultos para asegurar envío */}
						<Controller control={control} name="image.id" render={({ field }) => <input type="hidden" {...field} />} />
						<Controller
							control={control}
							name="image.name"
							render={({ field }) => <input type="hidden" {...field} />}
						/>
						<Controller
							control={control}
							name="image.image"
							render={({ field }) => <input type="hidden" {...field} />}
						/>

						<Grid item xs={12} md={6}>
							<Controller
								control={control}
								name="address"
								rules={{
									required: 'La dirección es requerida',
									maxLength: { value: 150, message: 'No puede superar 150 caracteres' },
								}}
								render={({ field }) => (
									<TextField
										{...field}
										fullWidth
										label="Dirección"
										required
										error={!!(errors as any).address}
										helperText={(errors as any).address?.message}
										inputProps={{ maxLength: 150 }}
									/>
								)}
							/>
						</Grid>

						<Grid item xs={12} md={6}>
							<Controller
								control={control}
								name="phoneNumber"
								rules={{ required: 'El teléfono es requerido' }}
								render={({ field }) => (
									<TextField
										{...field}
										fullWidth
										label="Teléfono"
										required
										error={!!(errors as any).phoneNumber}
										helperText={(errors as any).phoneNumber?.message}
									/>
								)}
							/>
						</Grid>

						<Grid container item xs={12} spacing={2} alignItems="center">
							{/* Latitud */}
							<Grid item xs={12} md={3}>
								<Controller
									control={control}
									name="lat"
									rules={{
										required: 'Latitud requerida',
										validate: (v) => {
											const num = Number(v);
											return (!isNaN(num) && num >= -90 && num <= 90) || 'Debe ser una latitud válida (-90 a 90)';
										},
									}}
									render={({ field }) => (
										<TextField
											required
											{...field}
											fullWidth
											label="Latitud"
											type="number"
											error={!!(errors as any).lat}
											helperText={(errors as any).lat?.message}
										/>
									)}
								/>
							</Grid>

							{/* Longitud */}
							<Grid item xs={12} md={3}>
								<Controller
									control={control}
									name="lng"
									rules={{
										required: 'Longitud requerida',
										validate: (v) => {
											const num = Number(v);
											return (!isNaN(num) && num >= -180 && num <= 180) || 'Debe ser una longitud válida (-180 a 180)';
										},
									}}
									render={({ field }) => (
										<TextField
											required
											{...field}
											fullWidth
											label="Longitud"
											type="number"
											error={!!(errors as any).lng}
											helperText={(errors as any).lng?.message}
										/>
									)}
								/>
							</Grid>

							{/* Botón Mapa */}
							<Grid
								item
								xs={12}
								md="auto"
								sx={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: { xs: 'flex-start', md: 'flex-start' },
								}}
							>
								<Button
									variant="outlined"
									onClick={() => setMapOpen(true)}
									startIcon={<LocationOnIcon />}
									sx={{ height: '55px' }}
								>
									Mapa
								</Button>
							</Grid>

							{/* Email */}
							<Grid item xs={12} md>
								<Controller
									control={control}
									name="email"
									rules={{
										required: 'Email requerido',
										pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Email inválido' },
										maxLength: { value: 255, message: 'No puede superar 255 caracteres' },
									}}
									render={({ field }) => (
										<TextField
											{...field}
											fullWidth
											label="Email"
											required
											error={!!(errors as any).email}
											helperText={(errors as any).email?.message}
											inputProps={{ maxLength: 255 }}
										/>
									)}
								/>
							</Grid>
						</Grid>

						<MapPickerModal
							open={mapOpen}
							onClose={() => setMapOpen(false)}
							initialLat={Number(watch('lat')) || configToMap.latitude}
							initialLng={Number(watch('lng')) || configToMap.longitude}
							countryCodeToSearchPosition={configToMap.countryCodeRestrictToSearch}
							onSelect={(lat, lng) => {
								setValue('lat', lat.toFixed(6));
								setValue('lng', lng.toFixed(6));
								trigger(['lat', 'lng']);
							}}
						/>

						{/* Imagen (Preview + Upload) */}
						<Grid item xs={12}>
							<Typography variant="subtitle1" gutterBottom>
								Imagen *
							</Typography>
							<Card variant="outlined" sx={{ maxWidth: 520 }}>
								<CardContent>
									<Stack direction="row" spacing={2} alignItems="center">
										<Box
											sx={{
												width: 120,
												height: 80,
												borderRadius: 1,
												overflow: 'hidden',
												bgcolor: '#f5f5f5',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												border: '1px solid #eee',
											}}
										>
											{previewUrl ? (
												<img
													src={previewUrl}
													alt={currentImage?.name || 'Preview'}
													style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
												/>
											) : (
												<Typography variant="caption" color="text.secondary">
													Sin imagen
												</Typography>
											)}
										</Box>

										<Box>
											<Typography variant="body2" sx={{ fontWeight: 500 }}>
												{currentImage?.name || '—'}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												{hasLocalUpload ? 'Imagen reemplazada (pendiente de guardar)' : 'Imagen original'}
											</Typography>
										</Box>
									</Stack>
								</CardContent>
								<CardActions sx={{ pl: 2, pb: 2 }}>
									<input
										key={uploadKey}
										id="branch-image-upload"
										type="file"
										accept="image/*"
										style={{ display: 'none' }}
										onChange={handleFileSelected}
									/>
									<Button size="small" startIcon={<UploadIcon />} onClick={handleUploadClick}>
										Subir imagen
									</Button>
									{originalImageRef.current && hasLocalUpload && (
										<Button size="small" color="inherit" startIcon={<RestoreIcon />} onClick={handleRevertImage}>
											Revertir cambios
										</Button>
									)}
								</CardActions>
							</Card>
						</Grid>

						{/* Engines */}
						<Grid item xs={12}>
							<Controller
								control={control}
								name="engines"
								rules={{ required: 'Seleccioná al menos un motor' }}
								render={({ field }) => {
									return (
										<FormControl fullWidth>
											<InputLabel id="engines-label" required>
												Motores habilitados
											</InputLabel>

											<Select
												// ⬇️ forzamos que SIEMPRE sea array
												value={Array.isArray(field.value) ? field.value : []}
												onChange={(e) => field.onChange(e.target.value as EngineEnum[])}
												labelId="engines-label"
												label="Motores habilitados"
												multiple
												input={<OutlinedInput label="Motores habilitados" />}
												renderValue={(selected) => (
													<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
														{(selected as EngineEnum[]).map((val) => {
															const meta = ENGINE_META[val];
															const Ic = meta.Icon;
															return (
																<Chip
																	key={val}
																	label={meta.label}
																	icon={<Ic sx={{ fontSize: 18, color: meta.color }} />}
																	variant="outlined"
																	sx={{
																		borderColor: meta.color,
																		bgcolor: () => alpha(meta.color, 0.08),
																		'& .MuiChip-label': { fontWeight: 500 },
																	}}
																/>
															);
														})}
													</Box>
												)}
												MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
											>
												{ENGINE_OPTIONS.map((opt) => {
													const meta = ENGINE_META[opt.value];
													const Ic = meta.Icon;
													const isChecked = (Array.isArray(field.value) ? field.value : []).includes(opt.value);

													return (
														<MenuItem key={opt.value} value={opt.value} dense>
															<ListItemIcon sx={{ minWidth: 36 }}>
																<Ic sx={{ color: meta.color }} />
															</ListItemIcon>
															<ListItemText primary={meta.label} primaryTypographyProps={{ sx: { fontWeight: 500 } }} />
															<Checkbox
																edge="end"
																checked={isChecked}
																sx={{ color: meta.color, '&.Mui-checked': { color: meta.color } }}
															/>
														</MenuItem>
													);
												})}
											</Select>

											{(errors as any).engines && (
												<Typography color="error" variant="caption">
													{(errors as any).engines?.message}
												</Typography>
											)}
										</FormControl>
									);
								}}
							/>
						</Grid>

						{/* Schedules */}
						<Grid item xs={12}>
							<Box display="flex" alignItems="center" justifyContent="space-between">
								<Typography variant="subtitle1">Horarios de apertura</Typography>
								<Button
									variant="outlined"
									onClick={() => {
										// id 0 para nuevos  // <-- cambio
										appendSchedule({
											id: 0,
											weekDay: WeekDayEnum.MONDAY,
											initialTime: '09:00',
											endTime: '18:00',
										} as any);
										trigger('schedules');
									}}
									disabled={usedWeekDays.size >= 7}
								>
									Agregar horario
								</Button>
							</Box>
							{scheduleFields.length === 0 && (
								<Typography color="error" variant="caption" sx={{ mt: 1 }}>
									Debe agregar al menos un horario antes de guardar.
								</Typography>
							)}
						</Grid>

						{scheduleFields.map((fieldItem, index) => {
							const usedExceptCurrent = new Set(
								(schedulesWatch ?? [])
									.map((s, i) => (i === index ? undefined : s?.weekDay))
									.filter(Boolean) as WeekDayEnum[],
							);
							return (
								<Grid container item spacing={2} xs={12} key={fieldItem.key}>
									<Controller
										control={control}
										name={`schedules.${index}.id` as const}
										render={({ field }) => <input type="hidden" {...field} />}
									/>
									<Grid item xs={12} md={4}>
										<Controller
											control={control}
											name={`schedules.${index}.weekDay` as const}
											rules={{
												required: 'Día requerido',
												validate: (v) => {
													if (v === undefined || v === null) return 'Día requerido';
													if (usedExceptCurrent.has(v as any)) return 'Ya existe un horario para este día';
													return true;
												},
											}}
											render={({ field }) => (
												<FormControl fullWidth>
													<InputLabel id={`weekday-label-${index}`} required>
														Día
													</InputLabel>
													<Select
														{...field}
														labelId={`weekday-label-${index}`}
														label="Día"
														onChange={(e) => {
															field.onChange(e);
															trigger('schedules');
														}}
													>
														{WEEK_DAY_OPTIONS.map((opt) => (
															<MenuItem
																key={opt.value}
																value={opt.value}
																disabled={usedExceptCurrent.has(opt.value as any)}
															>
																{opt.label}
															</MenuItem>
														))}
													</Select>
													{(errors as any)?.schedules?.[index]?.weekDay && (
														<Typography color="error" variant="caption">
															{(errors as any)?.schedules?.[index]?.weekDay?.message}
														</Typography>
													)}
												</FormControl>
											)}
										/>
									</Grid>

									<Grid item xs={12} md={3}>
										<Controller
											control={control}
											name={`schedules.${index}.initialTime` as const}
											rules={{
												required: 'Hora inicial requerida',
												pattern: { value: /^([01]\d|2[0-3]):[0-5]\d$/, message: 'Formato HH:mm' },
											}}
											render={({ field }) => (
												<TextField
													{...field}
													fullWidth
													label="Hora inicial (HH:mm)"
													error={!!(errors as any)?.schedules?.[index]?.initialTime}
													helperText={(errors as any)?.schedules?.[index]?.initialTime?.message}
												/>
											)}
										/>
									</Grid>

									<Grid item xs={12} md={3}>
										<Controller
											control={control}
											name={`schedules.${index}.endTime` as const}
											rules={{
												required: 'Hora fin requerida',
												pattern: { value: /^([01]\d|2[0-3]):[0-5]\d$/, message: 'Formato HH:mm' },
												validate: (v) => {
													const start = (watch(`schedules.${index}.initialTime`) as unknown as string) || '';
													return !start || v > start || 'La hora fin debe ser mayor a la inicial';
												},
											}}
											render={({ field }) => (
												<TextField
													{...field}
													fullWidth
													label="Hora fin (HH:mm)"
													error={!!(errors as any)?.schedules?.[index]?.endTime}
													helperText={(errors as any)?.schedules?.[index]?.endTime?.message}
												/>
											)}
										/>
									</Grid>

									<Grid item xs={12} md={2} display="flex" alignItems="center">
										<IconButton
											color="error"
											onClick={() => {
												removeSchedule(index);
												trigger('schedules');
											}}
										>
											<DeleteIcon />
										</IconButton>
									</Grid>
								</Grid>
							);
						})}
					</>
				)}

				{/* ========= No física ========= */}
				{!isPhysicalBranch && (
					<>
						<Grid item xs={12}>
							<Typography variant="h6">Datos de sucursal no física</Typography>
						</Grid>

						<Grid item xs={12} md={6}>
							<Controller
								control={control}
								name="onlyAdmins"
								render={({ field }) => (
									<FormControlLabel
										label="Solo administradores"
										control={
											<Switch
												checked={!!field.value}
												onChange={(_, checked) => field.onChange(checked)}
												onBlur={field.onBlur}
												inputRef={field.ref}
											/>
										}
									/>
								)}
							/>
						</Grid>

						{/* currencyConfigs */}
						<Grid item xs={12}>
							<Box display="flex" alignItems="center" justifyContent="space-between">
								<Typography variant="subtitle1">Configuraciones de moneda</Typography>
								<Button
									variant="outlined"
									onClick={() => {
										appendCurrency({ currency: '', netSuiteAccountId: '' } as any);
										trigger('currencyConfigs');
									}}
									disabled={distinctCurrencies.length === usedCurrencies.size}
									title={
										distinctCurrencies.length === usedCurrencies.size
											? 'Ya usaste todas las monedas disponibles'
											: undefined
									}
								>
									Agregar moneda
								</Button>
							</Box>
							{currencyFields.length === 0 && (
								<Typography color="error" variant="caption" sx={{ mt: 1 }}>
									Debe agregar al menos una configuración de moneda antes de guardar.
								</Typography>
							)}
						</Grid>

						{currencyFields.map((fieldItem, index) => {
							const currentCurrency: string | undefined = currencyConfigsWatch?.[index]?.currency ?? '';
							const usedExceptCurrent = new Set(
								(currencyConfigsWatch ?? [])
									.map((cc, i) => (i === index ? '' : (cc?.currency ?? '').trim()))
									.filter(Boolean),
							);
							const availableCurrencies = distinctCurrencies.filter((c) => !usedExceptCurrent.has(c));
							const accountsForCurrency = getAccountsByCurrency(netsuiteAccounts, currentCurrency);

							return (
								<Grid container item spacing={2} xs={12} key={fieldItem.id}>
									<Grid item xs={12} md={4}>
										<Controller
											control={control}
											name={`currencyConfigs.${index}.currency` as const}
											rules={{
												required: 'Moneda requerida',
												validate: (v) => {
													if (!v) return 'Moneda requerida';
													if (usedExceptCurrent.has(v)) return 'La moneda ya fue agregada';
													return true;
												},
											}}
											render={({ field }) => (
												<FormControl fullWidth>
													<InputLabel id={`cc-currency-label-${index}`} required>
														Moneda
													</InputLabel>
													<Select
														{...field}
														labelId={`cc-currency-label-${index}`}
														label="Moneda"
														onChange={(e) => {
															// @ts-ignore
															setValue(`currencyConfigs.${index}.netSuiteAccountId` as const, '' as any, {
																shouldValidate: true,
																shouldDirty: true,
															});
															field.onChange(e);
															trigger('currencyConfigs');
														}}
													>
														{availableCurrencies.map((c) => (
															<MenuItem key={c} value={c}>
																{c}
															</MenuItem>
														))}
													</Select>
													{(errors as any)?.currencyConfigs?.[index]?.currency && (
														<Typography color="error" variant="caption">
															{(errors as any)?.currencyConfigs?.[index]?.currency?.message}
														</Typography>
													)}
												</FormControl>
											)}
										/>
									</Grid>

									<Grid item xs={12} md={6}>
										<Controller
											control={control}
											name={`currencyConfigs.${index}.netSuiteAccountId` as const}
											rules={{
												required: 'NetSuite Account requerido',
												validate: (v) => {
													if (!v) return 'NetSuite Account requerido';
													const id = Number(v);
													const ok = accountsForCurrency.some((a) => a.id === id);
													return ok || 'La cuenta no coincide con la moneda seleccionada';
												},
											}}
											render={({ field }) => (
												<FormControl fullWidth>
													<InputLabel id={`cc-account-label-${index}`} required>
														NetSuite Account
													</InputLabel>
													<Select
														{...field}
														labelId={`cc-account-label-${index}`}
														label="NetSuite Account"
														disabled={!currentCurrency}
														onChange={(e) => {
															field.onChange(e);
															trigger('currencyConfigs');
														}}
													>
														{accountsForCurrency.map((acc) => (
															<MenuItem key={acc.id} value={acc.id}>
																{accountLabel(acc)}
															</MenuItem>
														))}
													</Select>
													{(errors as any)?.currencyConfigs?.[index]?.netSuiteAccountId && (
														<Typography color="error" variant="caption">
															{(errors as any)?.currencyConfigs?.[index]?.netSuiteAccountId?.message}
														</Typography>
													)}
												</FormControl>
											)}
										/>
									</Grid>

									<Grid item xs={12} md={2} display="flex" alignItems="center">
										<IconButton
											color="error"
											onClick={() => {
												removeCurrency(index);
												trigger('currencyConfigs');
											}}
										>
											<DeleteIcon />
										</IconButton>
									</Grid>
								</Grid>
							);
						})}
					</>
				)}

				{/* Botones */}
				<Grid item xs={12} display="flex" gap={2} justifyContent="flex-end" mt={2}>
					{onCancel && (
						<Button onClick={onCancel} color="inherit" disabled={isSubmitting}>
							Cancelar
						</Button>
					)}
					<Button
						variant="contained"
						type="submit"
						disabled={
							isSubmitting ||
							!isValid ||
							(!isPhysicalBranch && currencyFields.length === 0) ||
							(isPhysicalBranch && scheduleFields.length === 0)
						}
					>
						{submitLabel}
					</Button>
				</Grid>
			</Grid>

			{/* Input file oculto para subir imagen */}
			<input
				key={uploadKey}
				id="branch-image-upload"
				type="file"
				accept="image/*"
				style={{ display: 'none' }}
				onChange={handleFileSelected}
			/>
		</Box>
	);
};
